import * as type from "./questionTypes";
import { teamNameInput } from "./questionContentUniversal";
import React from "react";
import DCMapOverlap from "./DCMapOverlap";

// Course cover / location imagery
import promenadeSunset from "../../images/promenade/courseCoverPics/PromenadeSunset.jpg";
import fidiView from "../../images/promenade/courseCoverPics/FidiView.jpg";
import bkBridgeSunset from "../../images/promenade/courseCoverPics/BkBridgeSunset.jpg";
import statueOfLiberty from "../../images/promenade/courseCoverPics/StatueOfLiberty.jpg";

// Question / answer imagery
import sunDialImg from "../../images/promenade/questions/SunDial.jpg";
import libraScalesImg from "../../images/promenade/questions/LibraScales.jpg";
import piscesAnswerImg from "../../images/promenade/questions/PicesAnswer.jpg";
import freewayLayersAnswerImg from "../../images/promenade/questions/FreewayLayersAnswer.png";
import tripleCantileverFromBottom from "../../images/promenade/questions/cantileverSidePerspective.png";
import benchDedicationImg from "../../images/promenade/questions/BenchDedication.jpg";
import kennethLynchEngravingImg from "../../images/promenade/questions/KennethLynchConnecticuteEngraving.jpg";
import bkBridgePlaque1877Img from "../../images/promenade/questions/BkBridgePlaque1877.jpeg";
import bkBridgeWithFlagImg from "../../images/promenade/questions/BkBridgeWithFlag.jpg";
import bkBridge1898Img from "../../images/promenade/questions/BkBridge1898.png";
import verizonBuildingImg from "../../images/promenade/questions/VerizonBuilding.jpg";
import leaningTowerOfNYImg from "../../images/promenade/questions/LeaningTowerOfNY.jpg";
import wavertreeAnswerImg from "../../images/promenade/questions/WavertreeAnswer.jpg";
import redhookCranesAnswerImg from "../../images/promenade/questions/RedhookCranesAnswer.jpg";
import oldPierOneImg from "../../images/promenade/questions/OldPierOne.jpg";
import pierPilingsImg from "../../images/promenade/questions/PierPilings.jpg";
import dedicationStoneBattleOfLongIslandImg from "../../images/promenade/questions/DedicationStoneBattleOfLongIsland.jpg";
import statenIslandFerryImg from "../../images/promenade/questions/StatenIslandFerry.png";
import whiteFlagsBkImg from "../../images/promenade/questions/whiteFlagsBk.webp";
import GovernorsIslandWhiteBuilding from "../../images/promenade/questions/GovernorsIslandWhiteBuilding.jpg";
import nyMap1766 from "../../images/promenade/questions/1766 map of NY.png";
import nyMapModern from "../../images/promenade/questions/Modern map of NY.png";

const northEntranceOfPromenade = {
  name: "North entrance of promenade",
  img: promenadeSunset,
};

const observationOfLandmarks = {
  name: "Observation of landmarks",
  img: fidiView,
};

const byFlagPole = {
  name: "By Flag Pole",
  img: bkBridgeSunset,
};

const getToBrooklynPromenade = {
  displayName: "North entrance of promenade",
  location: northEntranceOfPromenade,
  type: type.informational,
  image1: sunDialImg,
  content1: "You're at the Brooklyn Heights Promenade. The first set of questions are at the north entrance — look for the sundial to make sure you're in the right place.",
};

// -------- North entrance of promenade ---------

const sunDialJustice = {
  displayName: "Sun Dial: Justice",
  location: northEntranceOfPromenade,
  type: type.freeTextQuestion,
  //image1: sunDialImg,
  boldContent: "According to the sun, what time is justice served?",
  hint: "Look at the thick band on the sundial that has Roman numerals and zodiac signs. Specifically, look at ALL the zodiac signs.",
  answer: {
    answer: new RegExp("(1|one|libra|scales)"),
    answerInfo: 
    {
      image1: libraScalesImg,
      content1: "On the sundial's equator, you'll find the Roman numerals I through XII marking the hours. Directly across from the numeral I is Libra—the scales, a symbol of justice.",
    },
    heading: "Have you been to Court Street?",
    content1: "Brooklyn Heights is home to six courthouses, which is why one of the main streets in this area is called Court Street.",
    },
};

const sunDialMissingConstellation = {
  displayName: "Sun Dial: Missing Constellation",
  location: northEntranceOfPromenade,
  type: type.multipleChoice,
  content1: "You'll notice that the sundial's equator features all twelve zodiac signs, or at least it was supposed to.",
  boldContent:
    "What constellation is missing?",
  hint: "Look at the high point of the band for a gap where one of the Zodiacs was pried off.",
  specialContent: {
    initialCards: ["Gemini", "Pisces", "Aquarius", "Cancer"],
  },
  answer: {
    answer: "Pisces",
    image1: piscesAnswerImg,
    heading: "Pisces was stolen!",
    content1:
      "Pisces, represented by two fish, was pried off the sundial by a vandal, leaving the ring of zodiac signs incomplete.",
  },
};

const freewayLayers = {
  displayName: "Freeway Layers",
  location: northEntranceOfPromenade,
  type: type.freeTextQuestion,
  content1:
    "Like layers of cake, there are layers of traffic right beneath your feet!",
  boldContent: "How many separate roadways are below?",
  hint: "Look over the railing facing Manhattan and count how many distinct levels you can see.",
  answer: {
    answer: new RegExp("(3|three)"),
    answerInfo: {
      image1: freewayLayersAnswerImg,
      content1: "Look over the railing and you'll see three stacked roadways below you."
      },
    
    heading: "Three decks, at risk of collapse",
    content1:
      "The Brooklyn Heights Promenade rests on a triple cantilever — three roadway decks projecting outward with no support pillars beneath them.",
      image2: tripleCantileverFromBottom,
      content2:
      "Built in the 1950s for smaller vehicles and less traffic, the structure has long outlived its intended lifespan and now requires major repairs. However, politicians have been reluctant to close or re-route the expressway.",
  },
};

const benchPlaqueMarriage = {
  displayName: "Bench Plaque: Marriage",
  location: northEntranceOfPromenade,
  type: type.freeTextQuestion,
  totalPossiblePoints: 40,
  content1:
    "The weathered Promenade benches have seen a lot of history, but one asks about the future",
  boldContent: "What does it ask?",
  hint: "Look for a plaque a few benches down from the sundial area and read the full inscription.",
  captionContent: "Note: Sometimes this answer will be hidden by a stranger, so we've adjusted available points down. Please skip this question if you can't find it.",
  answer: {
    answer: new RegExp("(marry|proposal)"),
    answerInfo: {
      image1: benchDedicationImg,
      content1:
        "Walking down the the Promenade, away from the Sundial area, the fifth bench has a plaque that reads:\n\n\"To my Arianna,\nI will love you forever and always\nWill you marry me?\nDomenico\"",
    },
    heading: "A $5,000 proposal prop",
    content1:
      "Plaques like these are installed through the city’s adopt-a-bench program. A single plaque costs $5,000 and NYC parks maintain it for 10 years.",
  },
};

const benchOrigin = {
  displayName: "Bench Origin",
  location: northEntranceOfPromenade,
  type: type.freeTextQuestion,
  content1:
    "Fitting with a lot of New York’s history, the benches on the promenade weren’t born in Brooklyn but emigrated from another state.",
  boldContent: "Where are the benches from?",
  hint: "Check the cast-iron legs of the bench for a maker’s mark.",
  answer: {
    answer: new RegExp("(wilton|conne)"),
    answerInfo: {
      image1: kennethLynchEngravingImg,
      content1:
        'On the bench legs you can see the engraving “Kenneth Lynch & Sons, Wilton Conn.” — the benches come from Wilton, Connecticut.',
    },
    heading: "A bench design supporting New Yorkers for almost 100 years",
    content1:
      "Kenneth Lynch was an accomplished blacksmith who co-designed the bench you see here with Robert Moses in 1939 (Moses is the same city planner that designed the triple cantilever that you're standing on)",
    content2:
      "New York standardized park furniture so it would be durable, repairable, and replaceable — a piece of public design that has quietly lasted for generations.",
  },
};

const bridgePlaque = {
  displayName: "Bridge Plaque",
  location: northEntranceOfPromenade,
  type: type.freeTextQuestion,
  content1:
    "One of the most impressive landmarks you can see from the Promenade is the Brooklyn Bridge. But to learn more about its construction, don’t look at it, look at your feet.",
  boldContent: "Name one of the years the Brooklyn Bridge was under construction.",
  hint: "Look for a bronze relief plaque set into the sidewalk near the promenade railing at the bottom of the entrance ramp.",
  answer: {
    answer: new RegExp("(1869|187[0-9]|188[0-3])"),
    answerInfo: {
      image1: bkBridgePlaque1877Img,
      content1:
        "There is a bronze relief plaque laid into the promenade sidewalk by the fence that depicts the bridge under construction. The year 1877 is engraved in the bottom right corner of the scene.",
    },
    heading: "A woman secretly finished what the men in her family could not",
    content1:
      "Construction on the Brooklyn Bridge lasted 14 years, from 1869 to 1883, but it wasn't all smooth sailing.",
    content2: "The bridge was the vision of John Roebling, who unfortunately died early in the project. His son, Washington Roebling, took over but soon faced his own tragedy, becoming bedridden from \"the bends.\" Washington's wife, Emily, then secretly took on his role. Secrecy was required due to intense gender bias and because corrupt politicians were eager to replace the Roeblings with someone more amenable to graft.",
      content3: "When construction finished, the bridge was the longest suspension bridge in the world."
    },
};

// -------- Get to observation of landmarks ---------

const getToObservationOfLandmarks = {
  displayName: "Observation of landmarks",
  location: observationOfLandmarks,
  type: type.informational,
  image1: fidiView,
  content1: "The next questions are about what you can see from here — the skyline, bridges, buildings, and harbor.",
};

// -------- Far away (observation of landmarks) ---------

const bridgeFlag = {
  displayName: "Bridge Flag",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  image1: bkBridge1898Img,
  image1Caption: "The Brooklyn Bridge in 1898.",
  content1: "When the Brooklyn Bridge was first built, it was the tallest structure in the western hemisphere ",
  boldContent: "What is at the highest point of the bridge?",
  hint: "Look at the very top of each bridge tower, above the gothic arches, for something fluttering in the wind.",
  answer: {
    answer: new RegExp("(flag|american)"),
    answerInfo: {
      image1: bkBridgeWithFlagImg,
      content1: "An American flag flies above each tower of the Brooklyn Bridge, marking the highest visible point.",
    },
    image1: whiteFlagsBkImg,
    heading: "Artists embarrassed the NYPD",
    content1: "In 2014, two German artists secretly replaced the American flags atop the Brooklyn Bridge with white flags for a day. The stunt sparked a major security scandal for the NYPD because the bridge is considered critical infrastructure, and the artists demonstrated how easily someone could access without detection."
  },
};

const verizonBuilding = {
  displayName: "Verizon Building",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "I am a fortress-like tower across the water in Manhattan, and I appear windowless from the waist down.",
  boldContent: "What brand adorns my side?",
  hint: "To the right of the Brooklyn Bridge, look for a tall beige tower with a logo high up on the facade.",
  answer: {
    answer: new RegExp("(verizon)"),
    answerInfo: {
      image1: verizonBuildingImg,
      content1:
      "Just to the right of where the Brooklyn Bridge enters Manhattan, you can see a building that appears windowless from the waist down. The building is branded with the Verizon logo."
    },
    heading: "Manhattan's ugliest building",
    content1:
      "The Verizon building was originally built as a telephone switching center, where huge machines mechanically routed nearly every phone call in Manhattan. However, as technology advanced, the building was converted to offices. ",
      content2: "In 2012, the Daily Telegraph ranked the building as the \"ugliest building in the world,\" leading the owners to give the upper floors a facelift of more windows."
  },
};

const buildingUnderConstruction = {
  displayName: "Building Under Construction",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "I am a tall, thin tower standing between a shiny green friend and another with a zigzaggy tattoo.",
  boldContent: "How would you describe my current state?",
  hint: "Look for a building wrapped in scaffolding and cranes rather than finished glass.",
  answer: {
    answer: new RegExp("(construction|unfinished|not finished|incomplete|uncomplete|develop|skeletal|bare|derelict|abandon|being built|in progress|not complete|not completed|stalled|scaffolding|work in progress|wip|not done)"),
    
    answerInfo: {
      image1: leaningTowerOfNYImg,
      content1:
      "The tower is unfinished — construction has stalled.",
    },
      heading: "The ‘Leaning Tower of New York’ tilts three inches",
      content1:
      "Construction came to a halt in 2020 after foundation problems caused the building to tilt three inches, triggering lawsuits, financial losses, and bankruptcies.",
      content2: "There is still no confirmed timeline for construction to resume, with some estimates suggesting that fixing the foundation could cost more than the completed building would ultimately be worth."
    },
};

const oldBoat = {
  displayName: "Old Boat",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "From here, you can see all kinds of vehicles. One stands taller than the others, without ever taking flight.",
  boldContent: "What powers its motion?",
  hint: "It is a type of boat",
  answer: {
    answer: new RegExp("(wind|sail|sails)"),
    answerInfo: {
      image1: wavertreeAnswerImg,
      content1: "The tallest vessels in the harbor are sailboats, with several visiting throughout the year and one permanently docked at the South Street Seaport Museum."
    },
    heading: "One of the last iron sailing ships still floats in the harbor",
    content1:
      "The Wavertree is a historic iron-hulled sailing ship built in 1885 and is one of the last wrought-iron sailing vessels still afloat. Today, visitors can step aboard and explore this maritime relic at the South Street Seaport Museum.",
  },
};


const cranesLocation = {
  displayName: "Cranes Location",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "New York Harbor used to be packed with working docks and cranes, but from here you won’t see many container ships in Brooklyn.",
  boldContent: "Looking across the harbor, what U.S. state do you see most of the big shipping cranes in today?",
  hint: "Follow the cranes along the horizon line — past the Statue of Liberty.",
  answer: {
    answer: new RegExp("(jersey|nj)"),
    answerInfo: {
      image1: statueOfLiberty,
      content1:
      "Follow the cranes along the horizon past the Statue of Liberty. The massive container cranes you see are mostly not in New York—they’re across the harbor in New Jersey.",
      },
    heading: "Container shipping moved to New Jersey — and cut costs by 95%",
    content1:
      "When container shipping became standardized, ports needed deep water, wide berths, and large areas of inexpensive land for massive container yards. New Jersey had room to expand, so much of New York's heavy shipping activity moved across the harbor.",
      content2: "The containerization revolution cut shipping costs by roughly 95%, fueling the rise of globalization and the era of inexpensive consumer goods."
  },
};

const sportsPier = {
  displayName: "Sports Pier",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "I am home to basketball courts, pickleball courts, a roller rink, and sports of other varieties.",
  boldContent: "What is my number?",
  hint: "Near where each  pier connects to the land, each pier has an elevated metal beam or roof where white lettering identifies its number. ",
  answer: {
    answer: new RegExp("(pier 2|2|two)"),
    answerInfo: {
      image1: statueOfLiberty,
      content1:
        "That’s Pier 2 in Brooklyn Bridge Park, a multi-sport playground floating right above the water.",
      },
    heading: "From industrial wasteland to playground",
    content1: "Before becoming a place for recreation, the piers of Brooklyn Bridge Park were part of a bustling industrial waterfront. When shipping operations moved to New Jersey in the 1970s, the shoreline was left behind, becoming a neglected and blighted area for decades.",
    content2: "Grassroots campaigns eventually led to the creation of Brooklyn Bridge Park, with redevelopment beginning in 2008."
  },
};

const craneColor = {
  displayName: "Crane Color",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "For the shipping cranes that remain in New York:",
  boldContent: "Name any one of the three patriotic colors that they are painted",
  answer: {
    answer: new RegExp("(red|white|blue)"),
    answerInfo: {
      image1: redhookCranesAnswerImg,
      content1: "The cranes are painted red, white, and blue."
    },
    heading: "Red Hook's name is Dutch — like half of New York",
    content1:
      "The remaining cranes in New York sit in Red Hook, a neighborhood whose name comes from a Dutch phrase referencing the red soil in the area.",
      content2: "The Dutch were the first Europeans to colonize this area and their legacy lives on with Dutch names all over NYC. For example, Brooklyn is named after a town in the Netherlands called Breukelen. Other notable Dutch-legacy names include Harlem, Staten Island, Coney Island, Bushwick, Stuyvesant, and more."
  },
};

const missingPier = {
  displayName: "Missing Pier",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "Each pier along the Brooklyn waterfront is labeled with a number—but if you’re paying close attention, you’ll notice one is missing.",
  boldContent: "Which pier number is missing?",
  hint: "Scan the pier numbers in order and see which one you never spot.",
  answer: {
    answer: new RegExp("(4|four|1|one)"),
    answerInfo: {
      content1:
      "There are two correct answers, both Pier 1 and Pier 4 are missing. You can tell Pier 1 is missing by all the old pier pilings that are to the right of Pier 2 and you can identify Pier 4 as missing since the labels jump from Pier 3 to Pier 5.",
    },
    image1: pierPilingsImg,
    heading: "Those rotting pilings are doing the harbor a favor",
    content1:
      "Looking at the remnants of the old pier, you may wonder: why haven't the pilings been removed?",
    content2: "Removing them could stir up polluted sediment trapped beneath the riverbed, so they have been left in place. The pilings also provide some benefits, acting as wave breaks and creating habitat for fish, birds, and oysters.",
  },
};

const oldPierOne = {
  // this question is removed
  displayName: "Old Pier 1",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  //image1: oldPierOneImg,
  content1:
    "To the right of Pier 2, you can see a forest of old wooden pilings — the remains of Old Pier 1 — sticking up out of the water.",
  boldContent: "About how many old pier pilings are there? Answers within 30 count.",
  //hint: "You don’t have to be perfect, but make your best estimate. Answers in a reasonable range count.",
  answer: {
    // Accept answers roughly between 170 and 230
    answer: new RegExp("^(1[7-9][0-9]|2[0-2][0-9])$"),
    answerInfo: {
      image1: oldPierOneImg,
      content1:
      "There are roughly 200 pilings left from Old Pier 1 — enough that counting them exactly is a challenge.",
    },
    content1:
      "A common question people ask is 'why don't they remove the pilings?'.",
      content2: "Removing these pilings could stir up polluted sediment, so they’ve been left in place. They also have the benefit of acting as wave breaks and a habitat for fish, birds, and oysters."
  },
};

const governorsIsland = {
  displayName: "Governors Island",
  location: observationOfLandmarks,
  type: type.multipleChoice,
  content1:
    "To the south, just to the left of the Statue of Liberty, you’ll see Governors Island. One building there stands out as a large white monolith.",
  boldContent:
    "Guess the purpose of the large white building:",
  specialContent: {
    initialCards: [
      "Ventilation shaft",
      "Military purpose",
      "Electrical substation",
      "Lighthouse",
    ],
  },
  answer: {
    answer: "Ventilation shaft",
    image1: GovernorsIslandWhiteBuilding,
    heading: "That white building is the lung for the Battery Tunnel",
    content1:
      "The white structure is a ventilation shaft for the Battery Tunnel, connecting Manhattan and Brooklyn. Inside are massive fans that entirely refresh the tunnel's air every 90 seconds.",
  },
};

const statenIslandFerry = {
  displayName: "Staten Island Ferry",
  location: observationOfLandmarks,
  type: type.freeTextQuestion,
  content1:
    "On the very tip of Manhattan, you’ll see a Ferry terminal.",
  boldContent:
    "What color are the largest ferries going in and out of this terminal?",
  hint: "Look for the bright boats carrying passengers between Manhattan and Staten Island. You may have to wait a minute or two.",
  answer: {
    answer: new RegExp("(orange)"),
    image1: statenIslandFerryImg,
    heading: "“Municipal Orange”",
    content1:
      "The Staten Island ferries are painted a bright orange, officially called “Municipal Orange,” so they stay visible even in fog and snow.",
    content2:
      "The Staten Island Ferry is free to ride and carries millions of passengers every year across New York Harbor.",
  },
};

// -------- Get to by flag pole ---------

const getToByFlagPole = {
  displayName: "By flag pole",
  location: byFlagPole,
  type: type.informational,
  content1: "For the next set of questions, continue along the promenade until you reach the flag pole at the Montague Street entrance.",
};

// -------- On the path ---------

const georgeWashingtonStone = {
  displayName: "George Washington Stone",
  location: byFlagPole,
  type: type.freeTextQuestion,
  content1:
    "In the afternoon, the American flag casts a shadow near a stone marking a moment that made the flag possible.",
  boldContent:
    "What battle does this rock honor?",
  hint: "Look for a stone with a metal tablet in the garden behind the flag pole.",
  answer: {
    answer: new RegExp("(long|battle of long island|long island)"),
    answerInfo: {
      image1: dedicationStoneBattleOfLongIslandImg,
      content1: "In the afternoon, the sun sits to the west (toward Manhattan), causing the flagpole to cast a shadow toward the street behind the Promenade.",
      content2:
      "Halfway to the street, in the garden area, you'll find a stone with an inlaid plaque commemorating the site of \"Four Chimneys\"—the house that served as George Washington's headquarters during the Battle of Long Island.",
    },
      heading: "Washington lost the battle but won the war",
      content1:
      "From this spot, George Washington commanded some 12,000 citizen soldiers against a professional British force of 32,000 and was ordered to hold New York at all costs.",
    content2:
      "Though the Americans were defeated in the battle of Brooklyn, a daring nighttime retreat allowed the Continental Army to escape across the East River and continue the fight for independence, ultimately winning the war and making the United States of America independent from Britain.",
  },
};

const plaque1880 = {
  displayName: "1880 Plaque",
  location: byFlagPole,
  type: type.freeTextQuestion,
  content1:
    "In front of the flagpole, the Promenade has a set of bronze reliefs set into the pavers showing how the view of Manhattan has changed over time.",
  boldContent:
    "What is one thing on the 1880s plaque that none of the other plaques have?",
  hint: "Look at what’s coming out of the ships in the harbor.",
  answer: {
    answer: new RegExp("(steam|smoke)"),
    answerInfo: {
    content1:
      "The 1880 plaque shows steam or smoke pouring from the ships — a nod to the age of steam power.",
      },
    heading: "Manhattan still runs on steam",
    content1:
      "These four plaques quietly trace how New York's sources of power (and its priorities) have changed over time: from wind-powered sailboats, to steam engines, to diesel ships, and finally to a waterfront where boats have nearly disappeared from the landscape.",
    content2:
      "Yet steam never fully left New York. More than 100 miles of underground steam pipes still heat much of Manhattan today, meaning the city continues to rely on a power system that began more than a century ago.",
  },
};

const plaqueChangesOverTime = {
  displayName: "Plaque Changes Over Time",
  location: byFlagPole,
  type: type.multipleChoice,
  content1:
    "Compare the changes between the plaques of 1776 and 2001.",
  boldContent:
    "Besides taller buildings, how did Manhattan change?",
  hint: "Examine the amount of water",
  specialContent: {
    initialCards: [
      "The East River grew wider",
      "There are fewer birds",
      "Streets got narrower",
      "Manhattan got more land",
    ],
  },
  answer: {
    answer: "Manhattan got more land",
    answerInfo: {
      content1: "The shoreline gradually moved outward over time—notice how far the water's edge has shifted.",
    },
    heading: "NYC didn’t just grow up, it also grew out",
    content1:
      "Much of Lower Manhattan was built on landfill, extending the shoreline closer to Brooklyn and is one reason the area is especially vulnerable to flooding.",
    content2:
      'Both Brooklyn and Manhattan have a "Water Street," a reminder of where the shoreline once stood before land was added to expand the city.',
  },
};

const nyMapsOverlap = {
  displayName: "NYC shoreline then and now",
  location: observationOfLandmarks,
  type: type.informational,
  content1: (
    <DCMapOverlap
      oldMapSrc={nyMap1766}
      newMapSrc={nyMapModern}
      oldAlt="1766 map of New York"
      newAlt="Modern map of New York"
      descriptionTop=" "
      descriptionBottom="Toggle between the 1766 and modern maps to see how much of today's NYC waterfront sits on man‑made land created with landfill."
      leftLabel="1766"
      rightLabel="Today"
    />
  ),
};

export const brooklynPromenadeCourseContent = {
  getToBrooklynPromenade,
  sunDialJustice,
  sunDialMissingConstellation,
  freewayLayers,
  bridgePlaque,
  benchPlaqueMarriage,
  benchOrigin,

  getToObservationOfLandmarks,
  bridgeFlag,
  verizonBuilding,
  buildingUnderConstruction,
  oldBoat,
  cranesLocation,
  sportsPier,
  craneColor,
  missingPier,
  //oldPierOne,
  governorsIsland,
  statenIslandFerry,
  
  getToByFlagPole,
  georgeWashingtonStone,
  plaque1880,
  plaqueChangesOverTime,
  nyMapsOverlap,

  teamNameInput,
};

